// Drone source frames: deterministic per-defect source sets, the popover
// strip, and the fullscreen gallery (view / add / bin modes).
import { useEffect, useState } from "react";
import type { ApiPhoto } from "../data/api";
import { IcChev, IcDownload, IcExternal, IcImage, IcMaximize, IcPlus, IcTrash, IcUndo, IcX } from "./icons";
import type { Defect, SourceImage } from "./types";

// Placeholder imagery for now — swap SOURCE_FRAMES for real captures.
const SOURCE_FRAMES = Array.from({ length: 16 }, (_, i) => `/assets/sources/frame-${String(i + 1).padStart(2, "0")}.png`);
export const SOURCE_CATALOG: SourceImage[] = SOURCE_FRAMES.map((src, i) => ({ id: "img-" + String(i + 1).padStart(2, "0"), src }));
export const getSourceById = (id: string): SourceImage | undefined => SOURCE_CATALOG.find((s) => s.id === id);

// Real evidence resolver: a defect's sourceImages basenames resolve through the
// inspection's photos (med thumb for display, raw URL for the original) — the
// same mapping the defects workspace uses. Returns [] when a photo is missing,
// never the fake SOURCE_CATALOG frames. Share the built resolver across a panel.
export function makeSourceResolver(photos: ApiPhoto[] | null | undefined): (d: Defect | null | undefined) => SourceImage[] {
  const byName = new Map<string, SourceImage>();
  for (const p of photos ?? []) byName.set(p.basename, { id: p.basename, src: p.thumbMed ?? p.thumbSmall ?? p.url, full: p.url });
  return (d) => (d?.sourceImages ?? [])
    .map((b) => byName.get(b.replace(/\.[A-Za-z0-9]+$/, "")))
    .filter((v): v is SourceImage => !!v);
}

function hashStr(s: unknown): number {
  let h = 2166136261;
  const str = String(s);
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

// Each defect surfaces the closest 1-8 drone captures nearest its pin.
// Deterministic per defect so the default set is stable across renders.
export function getSources(d: Defect | null | undefined): SourceImage[] {
  if (!d) return [];
  const seed = hashStr((d.id != null ? d.id : d.ref) + "");
  const count = 1 + (seed % 8);
  const start = seed % SOURCE_CATALOG.length;
  const out: SourceImage[] = [];
  for (let i = 0; i < count; i++) {
    const r = hashStr(seed + ":" + i);
    out.push({ ...SOURCE_CATALOG[(start + i * 3) % SOURCE_CATALOG.length]!, dist: 0.2 + (r % 34) / 10 });
  }
  out.sort((a, b) => (a.dist ?? 0) - (b.dist ?? 0));
  return out;
}

export function SourceStrip({ sources, onOpen, calculating, readOnly }: {
  sources: SourceImage[];
  onOpen: (index: number) => void;
  calculating?: boolean;
  readOnly?: boolean;
}) {
  const [hExp, setHExp] = useState(false);
  const n = sources.length;
  const many = n > 4;
  const thumbs = many ? sources.slice(0, 3) : sources.slice(0, 4);
  if (calculating) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 1, padding: "8px 10px", borderRadius: 8, background: "var(--di-navy)", border: "1px solid var(--di-border)" }}>
        <span className="spin" style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.15)", borderTopColor: "var(--di-orange)", flexShrink: 0 }}></span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--di-muted)" }}>Calculating source images…</span>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 1 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--di-subtle)" }}>{n === 0 ? "No source images" : n + " source image" + (n === 1 ? "" : "s")}</span>
        {(!readOnly || n > 0) &&
          <button onClick={() => onOpen(0)} onMouseEnter={() => setHExp(true)} onMouseLeave={() => setHExp(false)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: hExp ? "var(--di-orange-hover)" : "var(--di-orange)", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: 0 }}>
            <IcMaximize size={11} color={hExp ? "var(--di-orange-hover)" : "var(--di-orange)"} />{n === 0 ? "Manage" : "Expand"}
          </button>}
      </div>
      {n > 0 && <div style={{ display: "flex", gap: 5 }}>
        {thumbs.map((s, i) =>
          <button key={i} onClick={() => onOpen(i)} title="Source image"
            style={{ flex: 1, aspectRatio: "4 / 3", padding: 0, border: "1px solid var(--di-border)", borderRadius: 6, overflow: "hidden", cursor: "pointer", background: "var(--di-ink)" }}>
            <img src={s.src} alt="Source" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </button>)}
        {many &&
          <button onClick={() => onOpen(3)} title={"View all " + n + " source images"}
            style={{ flex: 1, aspectRatio: "4 / 3", border: "1px solid var(--di-border)", borderRadius: 6, background: "var(--di-ink)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--di-muted-2)" }}>+{n - 3}</button>}
      </div>}
    </div>
  );
}

// ── Fullscreen source-frame gallery ──────────────────────────────────
function GalleryArrow({ left, onClick }: { left?: boolean; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ position: "absolute", [left ? "left" : "right"]: 18, top: "50%", transform: "translateY(-50%)", width: 46, height: 46, borderRadius: "50%", border: "1px solid var(--di-border)", background: h ? "var(--di-ink)" : "rgba(14,18,25,0.86)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 5 }}>
      <IcChev size={20} color="var(--di-text)" style={{ transform: left ? "rotate(90deg)" : "rotate(-90deg)" }} />
    </button>
  );
}

function GalleryModeBtn({ Ic, label, active, onClick, title, accent }: { Ic: typeof IcPlus; label: string; active?: boolean; onClick: () => void; title?: string; accent?: boolean }) {
  const [h, setH] = useState(false);
  const on = active || accent;
  return (
    <button onClick={onClick} title={title} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 8, cursor: "pointer", flexShrink: 0, border: `1px solid ${on ? "rgba(249,115,22,0.45)" : h ? "var(--di-input)" : "var(--di-border)"}`, background: accent ? (h ? "rgba(249,115,22,0.20)" : "rgba(249,115,22,0.12)") : active ? "rgba(249,115,22,0.10)" : h ? "rgba(255,255,255,0.04)" : "transparent", color: on ? "var(--di-orange)" : h ? "var(--di-text)" : "var(--di-muted)", fontFamily: "var(--font-sans)", fontSize: accent ? 12 : 8.5, fontWeight: accent ? 600 : 700, textTransform: accent ? "none" : "uppercase", letterSpacing: accent ? "0" : "0.07em", transition: "all 150ms ease" }}>
      <Ic size={accent ? 13 : 11} color="currentColor" />{label}
    </button>
  );
}

// High-quality original = the raw capture when the photo record carries it,
// else the displayed src.
const srcOriginalUrl = (s: SourceImage) => s.full ?? s.src;
const srcFileName = (s: SourceImage) => srcOriginalUrl(s).split("/").pop() ?? "source.jpg";
const openSource = (s: SourceImage) => { window.open(srcOriginalUrl(s), "_blank", "noopener"); };
const dlSource = (s: SourceImage) => {
  const a = document.createElement("a");
  a.href = srcOriginalUrl(s);
  a.download = srcFileName(s);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

function GalleryTile({ s, mode, onClick }: { s: SourceImage; mode: "add" | "restore"; onClick: () => void }) {
  const [h, setH] = useState(false);
  const Ic = mode === "add" ? IcPlus : IcUndo;
  const label = mode === "add" ? "Add" : "Restore";
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} title={mode === "add" ? "Add to this defect" : "Restore to this defect"}
      style={{ position: "relative", aspectRatio: "4 / 3", padding: 0, border: `1px solid ${h ? "var(--di-orange)" : "var(--di-border)"}`, borderRadius: 10, overflow: "hidden", cursor: "pointer", background: "var(--di-ink)", transition: "border-color 150ms ease" }}>
      <img src={s.src} alt="Source" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: h ? 0.75 : 1, transition: "opacity 150ms ease" }} />
      <span style={{ position: "absolute", right: 8, bottom: 8, display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 9px", borderRadius: 7, background: "rgba(14,18,25,0.92)", border: `1px solid ${h ? "rgba(249,115,22,0.45)" : "var(--di-border)"}`, fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: h ? "var(--di-orange)" : "var(--di-muted)" }}>
        <Ic size={10} color="currentColor" />{label}
      </span>
    </button>
  );
}

function GalleryEmpty({ Ic, title, hint }: { Ic: typeof IcImage; title: string; hint?: string }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center", padding: 24 }}>
      <div style={{ width: 52, height: 52, borderRadius: 13, background: "var(--di-surface)", border: "1px solid var(--di-border)", display: "flex", alignItems: "center", justifyContent: "center" }}><Ic size={22} color="var(--di-subtle)" /></div>
      <div>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--di-text)", marginBottom: 5 }}>{title}</div>
        {hint && <div style={{ fontSize: 12.5, color: "var(--di-subtle)", maxWidth: 330, lineHeight: 1.55 }}>{hint}</div>}
      </div>
    </div>
  );
}

export function SourceGallery({ defect, sources, bin, catalog, index, setIndex, onClose, onRemove, onAdd, onRestore, readOnly }: {
  defect: Defect;
  sources: SourceImage[];
  bin: SourceImage[];
  catalog: SourceImage[];
  index: number;
  setIndex: (i: number) => void;
  onClose: () => void;
  onRemove?: (id: string) => void;
  onAdd?: (id: string) => void;
  onRestore?: (id: string) => void;
  readOnly?: boolean;
}) {
  const [mode, setMode] = useState<"view" | "add" | "bin">("view");
  const [hRem, setHRem] = useState(false);
  const n = sources.length;
  const idx = n ? Math.min(index, n - 1) : 0;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { if (mode !== "view") setMode("view"); else onClose(); }
      else if (mode === "view" && n > 1 && e.key === "ArrowRight") setIndex((idx + 1) % n);
      else if (mode === "view" && n > 1 && e.key === "ArrowLeft") setIndex((idx - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, n, mode, onClose, setIndex]);
  const cur = sources[idx];
  const sub = mode === "add" ? "Add images · " + catalog.length + " available in catalogue" : mode === "bin" ? "Removed source images" : "Closest drone source images";
  const grid = (items: SourceImage[], tileMode: "add" | "restore", action?: (id: string) => void) => (
    <div style={{ flex: 1, overflowY: "auto", padding: 18, minHeight: 0 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10 }}>
        {items.map((s) => <GalleryTile key={s.id} s={s} mode={tileMode} onClick={() => action && action(s.id)} />)}
      </div>
    </div>
  );
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 1600, background: "rgba(6,9,13,0.94)", backdropFilter: "blur(10px)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 16px", borderBottom: "1px solid var(--di-border)", flexShrink: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><IcImage size={15} color="var(--di-orange)" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--di-orange)" }}>#{defect.ref}</span>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--di-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{defect.title}</span>
          </div>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--di-subtle)" }}>{sub}</span>
        </div>
        {!readOnly && <GalleryModeBtn Ic={IcPlus} label="Add images" active={mode === "add"} onClick={() => setMode(mode === "add" ? "view" : "add")} />}
        {!readOnly && <GalleryModeBtn Ic={IcTrash} label={"Bin" + (bin.length ? " · " + bin.length : "")} active={mode === "bin"} onClick={() => setMode(mode === "bin" ? "view" : "bin")} />}
        {mode === "view" && cur && <GalleryModeBtn accent Ic={IcExternal} label="Open High-Quality Original" title={"Open " + srcFileName(cur) + " (full size) in a new tab"} onClick={() => openSource(cur)} />}
        {mode === "view" && cur && <GalleryModeBtn accent Ic={IcDownload} label="Download High-Quality Original" title={"Download " + srcFileName(cur) + " (full size)"} onClick={() => dlSource(cur)} />}
        {mode === "view" && n > 0 && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--di-muted-2)", flexShrink: 0, marginLeft: 4 }}>{idx + 1} / {n}</span>}
        <button onClick={onClose} title="Close (Esc)" style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--di-border)", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--di-subtle)", flexShrink: 0 }}><IcX size={15} /></button>
      </div>
      {mode === "view" && (n === 0 || !cur
        ? <GalleryEmpty Ic={IcImage} title="No source images" hint="All images for this defect were removed. Add images from the catalogue, or restore them from the bin." />
        : <>
          <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", padding: "22px 74px", minHeight: 0 }}>
            <img src={cur.src} alt="Source" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 12, border: "1px solid var(--di-border)" }} />
            {!readOnly && <button onClick={() => onRemove && onRemove(cur.id)} onMouseEnter={() => setHRem(true)} onMouseLeave={() => setHRem(false)} title="Move this image to the bin"
              style={{ position: "absolute", top: 34, right: 86, zIndex: 6, display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 12px", borderRadius: 8, cursor: "pointer", background: "rgba(14,18,25,0.9)", backdropFilter: "blur(8px)", border: `1px solid ${hRem ? "rgba(239,68,68,0.45)" : "var(--di-border)"}`, color: hRem ? "var(--di-destructive)" : "var(--di-muted)", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", transition: "all 150ms ease" }}>
              <IcTrash size={11} color="currentColor" />Remove
            </button>}
            {n > 1 && <GalleryArrow left onClick={() => setIndex((idx - 1 + n) % n)} />}
            {n > 1 && <GalleryArrow onClick={() => setIndex((idx + 1) % n)} />}
          </div>
          {n > 1 &&
            <div style={{ display: "flex", gap: 8, padding: "11px 16px", borderTop: "1px solid var(--di-border)", overflowX: "auto", justifyContent: "center", flexShrink: 0 }}>
              {sources.map((s, i) =>
                <button key={s.id} onClick={() => setIndex(i)} title="Source image"
                  style={{ padding: 0, border: `2px solid ${i === idx ? "var(--di-orange)" : "transparent"}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", width: 94, height: 70, flexShrink: 0, background: "var(--di-ink)", opacity: i === idx ? 1 : 0.55, transition: "opacity 150ms ease" }}>
                  <img src={s.src} alt="Source" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </button>)}
            </div>}
        </>)}
      {mode === "add" && (catalog.length === 0
        ? <GalleryEmpty Ic={IcImage} title="No more images available" hint="Every source image from this survey is already attached to this defect or in its bin." />
        : grid(catalog, "add", onAdd))}
      {mode === "bin" && (bin.length === 0
        ? <GalleryEmpty Ic={IcTrash} title="Bin is empty" hint="Images you remove from this defect appear here and can be restored." />
        : grid(bin, "restore", onRestore))}
    </div>
  );
}
